/* Scoped custom gallery. No global variant listeners, polling, or DOM reordering. */
(() => {
  'use strict';
  if (customElements.get('aapka-product-gallery')) return;

  class AapkaProductGallery extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      clearTimeout(this.clickTimer);
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.panels?.forEach((panel) => this.pauseMedia(panel));
    }

    initialize() {
      this.events?.abort();
      this.resizeObserver?.disconnect();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.panels = Array.from(this.querySelectorAll('[data-gallery-media]'));
      this.scroller = this.querySelector('[data-gallery-thumbnail-scroller]');
      this.rail = this.querySelector('[data-gallery-rail]');
      this.main = this.querySelector('[data-gallery-main]');
      this.up = this.querySelector('[data-gallery-up]');
      this.down = this.querySelector('[data-gallery-down]');
      this.status = this.querySelector('[data-gallery-status]');
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.featuredId = this.dataset.initialMediaId;

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-gallery-thumb]');
        if (thumb && this.contains(thumb)) this.select(thumb.dataset.mediaId);
        if (event.target.closest('[data-gallery-up]')) this.scrollOne(-1);
        if (event.target.closest('[data-gallery-down]')) this.scrollOne(1);
      }, options);

      this.addEventListener('keydown', (event) => this.onKeyDown(event), options);
      this.scroller?.addEventListener('scroll', () => this.scheduleArrows(), {
        ...options,
        passive: true,
      });
      this.mobile.addEventListener('change', () => this.positionSelected('auto'), options);
      this.main?.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse') return;
        const active = this.panels.find((panel) => !panel.hidden);
        this.gesture = active?.dataset.mediaType === 'image'
          ? { x: event.clientX, y: event.clientY, id: event.pointerId }
          : null;
      }, { ...options, passive: true });
      this.main?.addEventListener('pointerup', (event) => this.onPointerUp(event), options);
      this.main?.addEventListener('pointercancel', () => { this.gesture = null; }, options);
      this.main?.addEventListener('click', (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.suppressClick = false;
      }, { ...options, capture: true });

      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleArrows());
        if (this.scroller) this.resizeObserver.observe(this.scroller);
        if (this.main) this.resizeObserver.observe(this.main);
      }
      const initial = this.panels.find((panel) => !panel.hidden) || this.panels[0];
      if (initial) this.select(initial.dataset.mediaId, { behavior: 'auto', announce: false });
      this.selectGalleryMedia = (id) => this.setActiveMedia(id);
    }

    get visibleThumbs() {
      return (this.thumbs || []).filter((thumb) => !thumb.hidden);
    }

    setActiveMedia(mediaId) {
      // May be called before the next animation frame during a section swap.
      if (!this.panels) {
        cancelAnimationFrame(this.frame);
        this.initialize();
      }
      const id = String(mediaId);
      if (!this.panels.some((panel) => panel.dataset.mediaId === id)) return;
      this.featuredId = id;
      this.select(id);
    }

    select(mediaId, { behavior = 'smooth', announce = true } = {}) {
      const id = String(mediaId);
      const selected = this.panels.find((panel) => panel.dataset.mediaId === id);
      if (!selected) return;
      this.activeId = id;

      this.thumbs.forEach((thumb) => {
        const isCurrent = thumb.dataset.mediaId === id;
        const isOtherVariant = this.dataset.hideVariants === 'true' &&
          thumb.dataset.variantMedia === 'true' &&
          thumb.dataset.mediaId !== this.featuredId && !isCurrent;
        thumb.hidden = isOtherVariant;
        thumb.classList.toggle('is-active', isCurrent);
        if (isCurrent) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.panels.forEach((panel) => {
        const active = panel === selected;
        if (!active && !panel.hidden) this.pauseMedia(panel);
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      const count = this.visibleThumbs.length;
      this.style.setProperty('--ae-visible-count', String(Math.max(1, Math.min(4, count))));
      this.classList.toggle('ae-amazon-gallery--single', count <= 1);
      if (this.rail) {
        this.rail.hidden = count <= 1;
        this.rail.dataset.overflow = String(count > 4);
      }
      if (announce && this.status) {
        const index = this.visibleThumbs.findIndex((thumb) => thumb.dataset.mediaId === id);
        this.status.textContent = `Product media ${Math.max(1, index + 1)} of ${Math.max(1, count)}`;
      }
      this.positionSelected(behavior);
      this.updateArrows();
    }

    pauseMedia(panel) {
      panel.querySelectorAll('video').forEach((video) => video.pause());
      panel.querySelectorAll('iframe').forEach((iframe) => {
        try {
          const host = new URL(iframe.src).origin;
          if (iframe.classList.contains('js-youtube')) {
            iframe.contentWindow?.postMessage(JSON.stringify({
              event: 'command', func: 'pauseVideo', args: [],
            }), host);
          } else if (iframe.classList.contains('js-vimeo')) {
            iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), host);
          }
        } catch (_) { /* A malformed external media URL must not break images. */ }
      });
      panel.querySelectorAll('product-model').forEach((model) => model.modelViewerUI?.pause());
    }

    scrollBehavior(preferred) {
      return this.reducedMotion.matches ? 'auto' : preferred;
    }

    positionSelected(behavior = 'smooth') {
      const thumb = this.visibleThumbs.find((item) => item.dataset.mediaId === this.activeId);
      if (!thumb || !this.scroller) return;
      const item = thumb.getBoundingClientRect();
      const view = this.scroller.getBoundingClientRect();
      const mobile = this.mobile.matches;
      const current = mobile ? this.scroller.scrollLeft : this.scroller.scrollTop;
      const delta = mobile ? item.left - view.left : item.top - view.top;
      const maximum = Math.max(0, mobile
        ? this.scroller.scrollWidth - this.scroller.clientWidth
        : this.scroller.scrollHeight - this.scroller.clientHeight);
      const target = mobile
        ? current + delta - (view.width - item.width) / 2
        : current + delta - 4;
      this.scroller.scrollTo({
        top: mobile ? 0 : Math.max(0, Math.min(target, maximum)),
        left: mobile ? Math.max(0, Math.min(target, maximum)) : 0,
        behavior: this.scrollBehavior(behavior),
      });
    }

    scrollOne(direction) {
      if (!this.scroller || this.mobile.matches || !this.visibleThumbs.length) return;
      const gap = parseFloat(getComputedStyle(this.scroller).rowGap) || 0;
      const step = this.visibleThumbs[0].getBoundingClientRect().height + gap;
      const currentIndex = Math.round(this.scroller.scrollTop / step);
      this.scroller.scrollTo({ top: (currentIndex + direction) * step, behavior: this.scrollBehavior('smooth') });
    }

    scheduleArrows() {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateArrows());
    }

    updateArrows() {
      if (!this.scroller || !this.up || !this.down) return;
      const maximum = Math.max(0, this.scroller.scrollHeight - this.scroller.clientHeight);
      const canUp = !this.mobile.matches && this.scroller.scrollTop > 1;
      const canDown = !this.mobile.matches && this.scroller.scrollTop < maximum - 1;
      [[this.up, canUp], [this.down, canDown]].forEach(([button, usable]) => {
        button.disabled = !usable;
        button.classList.toggle('is-hidden', !usable);
      });
    }

    onKeyDown(event) {
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (!thumb) return;
      const items = this.visibleThumbs;
      const index = items.indexOf(thumb);
      const previous = this.mobile.matches ? 'ArrowLeft' : 'ArrowUp';
      const next = this.mobile.matches ? 'ArrowRight' : 'ArrowDown';
      let destination;
      if (event.key === previous) destination = Math.max(0, index - 1);
      if (event.key === next) destination = Math.min(items.length - 1, index + 1);
      if (event.key === 'Home') destination = 0;
      if (event.key === 'End') destination = items.length - 1;
      if (destination === undefined || !items[destination]) return;
      event.preventDefault();
      items[destination].focus({ preventScroll: true });
      this.select(items[destination].dataset.mediaId);
    }

    onPointerUp(event) {
      const start = this.gesture;
      this.gesture = null;
      if (!start || start.id !== event.pointerId) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      const items = this.visibleThumbs;
      const index = items.findIndex((item) => item.dataset.mediaId === this.activeId);
      const target = items[index + (dx < 0 ? 1 : -1)];
      if (!target) return;
      this.suppressClick = true;
      clearTimeout(this.clickTimer);
      this.clickTimer = setTimeout(() => { this.suppressClick = false; }, 400);
      this.select(target.dataset.mediaId);
    }
  }

  customElements.define('aapka-product-gallery', AapkaProductGallery);
})();
